// ExtractedContentPanel - what the Files page shows for one upload when its row is
// opened: the summary and text the agents read, and what the extraction cost.
import { useUploadExtracted, ViewAiTraceButton } from "../api";
import { CollapsibleSection } from "./CollapsibleSection";
import { ExtractedTextSection } from "./ExtractedTextSection";
import { sizeLabel } from "./extractionCounts";

interface Props {
  uploadId: string;
  // Document size from the ROW, so the collapsed Text header can state it without
  // fetching the text record that knows it.
  chars?: number | null;
  pages?: number | null;
}

const BANNER_STYLE: React.CSSProperties = {
  padding: 10,
  fontSize: 13,
  color: "#6b7280",
  background: "#f9fafb",
  // The error often embeds a long unbroken filename; wrap it.
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

export const ExtractedContentPanel = ({ uploadId, chars, pages }: Props): JSX.Element => {
  const { data, isLoading, isError, error } = useUploadExtracted(uploadId);

  if (isLoading) {
    return (
      <div style={{ padding: 12, fontSize: 13, color: "#6b7280" }}>
        Loading extracted content…
      </div>
    );
  }
  if (isError) {
    return (
      <div style={{ padding: 12, fontSize: 13, color: "#b91c1c" }}>
        Failed to load extracted content:{" "}
        {error instanceof Error ? error.message : String(error)}
      </div>
    );
  }
  if (!data) return <></>;

  const statusBanner = (() => {
    if (data.extraction_status === "succeeded") return null;
    if (data.extraction_status === "unsupported") {
      return (
        <div style={BANNER_STYLE}>
          This file could not be extracted (unsupported content type, or an
          unreadable / rights-managed document).{" "}
          {data.extraction_error && <span>{data.extraction_error}</span>}
        </div>
      );
    }
    if (data.extraction_status === "failed") {
      return (
        <div style={{ ...BANNER_STYLE, color: "#991b1b", background: "transparent", border: "1px solid #f87171" }}>
          Extraction failed.{" "}
          {data.extraction_error && (
            <span style={{ color: "#7f1d1d" }}>{data.extraction_error}</span>
          )}
        </div>
      );
    }
    return <div style={BANNER_STYLE}>Extraction pending.</div>;
  })();

  // Summarised on the collapsed header so the two questions an operator opens
  // this for - what did it cost, and how old is it - are answered without opening.
  const detailsSummaryParts = [
    data.cost_usd !== null ? formatCostUsd(data.cost_usd) : null,
    data.extraction_attempted_at
      ? `on ${formatExtractedAt(data.extraction_attempted_at)}`
      : null,
  ]
    .filter(Boolean)
    .join(" ");
  const detailsSummary = detailsSummaryParts ? ` - ${detailsSummaryParts}` : "";
  const size = sizeLabel(chars, pages);

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        margin: "8px 0 16px",
        background: "white",
      }}
    >
      {statusBanner}
      {/* Open by default: it is the exact sentence the agents' document inventory
          lists this file by. */}
      <CollapsibleSection title="Summary" defaultExpanded>
        <div style={{ padding: "10px 12px", fontSize: 13, lineHeight: 1.5 }}>
          {data.summary ? (
            data.summary
          ) : (
            <span style={{ color: "#6b7280" }}>
              No summary - extraction produced none for this file.
            </span>
          )}
        </div>
      </CollapsibleSection>
      {/* What a read_document on this file returns to an agent. Mounted only on
          expand, so a closed section costs no fetch. */}
      {data.has_extracted_text && (
        <CollapsibleSection
          title={`Text${size ? ` - ${size}` : ""}`}
          defaultExpanded={false}
        >
          <ExtractedTextSection uploadId={uploadId} hasExtractedText />
        </CollapsibleSection>
      )}
      <CollapsibleSection title={`Extraction details${detailsSummary}`} defaultExpanded={false}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "10px 12px",
            fontSize: 12,
            color: "#6b7280",
          }}
        >
          {/* One button: the backend merges every trace part persisted under this
              upload's id (the extraction and any PDF-OCR pass) into one trace. */}
          <ViewAiTraceButton
            surface="uploads"
            artefactId={uploadId}
            label="View AI trace"
            hasTrace={data.has_ai_trace}
          />
          {data.cost_usd !== null && (
            <span title="Cost of this file's extraction (Bedrock tokens)">
              cost: {formatCostUsd(data.cost_usd)}
            </span>
          )}
          {data.extraction_attempted_at && (
            <span title={data.extraction_attempted_at}>
              extracted: {formatExtractedAt(data.extraction_attempted_at)}
            </span>
          )}
          {data.extraction_version && (
            <span>
              extractor: <code>{data.extraction_version}</code>
            </span>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

// Date-only: "2026-08-02" answers "is this stale?"; the full ISO value stays on
// the title attribute.
const formatExtractedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
};

// Sub-cent costs (the common case for one cheap extraction) show in a finer unit
// so they don't all read "$0.00".
const formatCostUsd = (usd: number): string => {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
};
